/**
 * @type {import('tailwindcss').Config}
 */

const defaultTheme = require('tailwindcss/defaultTheme');
const { semanticColorsConfig } = require('./src/configs/tailwind/color');

module.exports = {
  content: [
    './src/pages/**/*.{js,ts,jsx,tsx}',
    './src/common/components/**/*.{js,ts,jsx,tsx}',
    './src/common/containers/**/*.{js,ts,jsx,tsx}',
    './src/common/constants/*.{js,ts,jsx,tsx}',
    './src/modules/**/*.{js,ts,jsx,tsx}',
    './src/layouts/**/*.{js,ts,jsx,tsx}',
  ],
  safelist: [
    '!hidden',
    'md:lg:!flex',
    'md:lg:!hidden',
    'mr-1',
    { pattern: /^bg-link-/ },
  ],
  darkMode: 'class',
  theme: {
    colors: {
      // * None colors support
      inherit: 'inherit',
      current: 'currentColor',
      transparent: 'transparent',
      // We set the variant 50 as the default color
      white: '#ffffff',
      black: '#000000',
      green: {
        DEFAULT: '#00A796',
        '00': '#E0F4F2',
        10: '#B3E5E0',
        20: '#80D3CB',
        30: '#4DC1B6',
        40: '#26B4A6',
        50: '#00A796',
        60: '#009F8E',
        70: '#00806F',
        80: '#006658',
        90: '#004D40',
        100: '#156931',
        a10: '#A9FFEE',
        a20: '#76FFE5',
        a30: '#43FFDB',
        a40: '#2AFFD6',
        a50: '#11A699',
      },
      blue: {
        DEFAULT: '#004C6A',
        '00': '#E0EAED',
        10: '#E5F3F8',
        20: '#80A6B5',
        30: '#4D8297',
        40: '#266780',
        50: '#004C6A',
        60: '#004562',
        70: '#003C57',
        80: '#00334D',
        90: '#00243C',
        100: '#004B69',
        a10: '#52CEFF',
        a20: '#1ABEFF',
        a30: '#00ABFF',
        a40: '#0079F1',
        a50: '#3399FF',
      },
      red: {
        DEFAULT: '#F3716D',
        '00': '#FEEEED',
        10: '#FBD4D3',
        20: '#F9B8B6',
        30: '#F79C99',
        40: '#F58683',
        50: '#F3716D',
        60: '#F16965',
        70: '#EF5E5A',
        80: '#ED5450',
        90: '#EA423E',
        a10: '#FFD3D2',
        a20: '#FFBAB8',
        a30: '#B23F3C',
        a40: '#4D1B1A',
        a50: '#D94242',
      },
      gray: {
        white: '#FFFFFF',
        '00': '#F8F9FA',
        0o0: '#F0F2F5',
        10: '#F2F5F6',
        20: '#EAEEF0',
        30: '#E2E7EA',
        40: '#DBE1E6',
        50: '#D0D8DD',
        60: '#C4CED5',
        70: '#BAC5CD',
        80: '#B6BCC8',
        90: '#838A9A',
        100: '#717888',
        110: '#595F6D',
        120: '#444954',
        130: '#30353F',
        140: '#1F2227',
        150: '#282B2E',
        160: '#B7BEC7',
        170: '#565F6B',
        black: '#000000',
      },
      success: {
        DEFAULT: '#1E873C',
        10: '#E5F4E9',
        20: '#BDE3C8',
        30: '#92D0A4',
        40: '#0D7421',
      },
      error: {
        DEFAULT: '#DC3545',
        10: '#FBE4E5',
        20: '#F4BCBF',
        30: '#ED8F94',
        40: '#99080C',
      },
      warning: {
        DEFAULT: '#FFC107',
        10: '#FFF3CD',
        20: '#FFF2CC',
        30: '#FDD88E',
        40: '#553601',
        50: '#FFB114',
        100: '#8C541C',
      },
      link: {
        DEFAULT: '#007BFF',
        10: '#E0EFFF',
        20: '#B3DEF0',
        30: '#80C9E6',
        40: '#0063AD',
      },
      yellow: {
        DEFAULT: '#FAB347',
        '00': '#FFF9EF',
        10: '#FFF2D7',
        20: '#FFE7B8',
        30: '#FDD88E',
        40: '#FFC555',
        50: '#FAB347',
        60: '#E89635',
        70: '#CE7129',
        80: '#B24319',
        90: '#803012',
      },
    },
    boxShadow: {
      xs: '0px 1px 1px rgba(0, 0, 0, 0.15), 0px 0px 3px rgba(0, 0, 0, 0.1)',
      sm: '0px 2px 2px rgba(0, 0, 0, 0.15), 0px 1px 8px rgba(0, 0, 0, 0.1)',
      DEFAULT:
        '0px 8px 10px rgba(0, 0, 0, 0.1), 0px 3px 8px rgba(0, 0, 0, 0.15)',
      lg: '0px 4px 24px rgba(0, 0, 0, 0.1), 0px 8px 15px rgba(0, 0, 0, 0.15)',
      xl: '0px 6px 30px rgba(0, 0, 0, 0.1), 0px 16px 24px rgba(0, 0, 0, 0.15)',
      '2xl':
        '0px 6px 46px rgba(0, 0, 0, 0.1), 0px 24px 38px rgba(0, 0, 0, 0.15)',
      '3xl':
        '0px 8px 50px rgba(0, 0, 0, 0.1), 0px 30px 44px rgba(0, 0, 0, 0.15)',
      '4xl':
        '0px 8px 60px rgba(0, 0, 0, 0.1), 0px 36px 50px rgba(0, 0, 0, 0.15)',
    },
    fontSize: {
      xxs: ['11px', '18px'],
      xs: ['12px', '18px'],
      sm: ['14px', '22px'],
      base: ['16px', '28px'],
      lg: ['18px', '32px'],
      xl: ['20px', '32px'],
      '2xl': ['24px', '40px'],
      '3xl': ['28px', '48px'],
      '4xl': ['32px', '56px'],
      '5xl': ['40px', '72px'],
    },
    fontFamily: {
      sans: ['dana', ...defaultTheme.fontFamily.sans],
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      demibold: 600,
      bold: 700,
    },
    screens: {
      '3xs': '320px',
      // => @media (min-width: 320px) { ... }

      '2xs': '350px',
      // => @media (min-width: 350px) { ... }

      xs: '380px',
      // => @media (min-width: 380px) { ... }

      sm: '640px',
      // => @media (min-width: 640px) { ... }

      md: '768px',
      // => @media (min-width: 768px) { ... }

      lg: '1024px',
      // => @media (min-width: 1024px) { ... }

      xl: '1280px',
      // => @media (min-width: 1280px) { ... }

      '2xl': '1536px',
      // => @media (min-width: 1536px) { ... }
    },
    extend: {
      ...semanticColorsConfig,
      keyframes: {
        fillWidth: {
          '0%': {
            'max-width': 0,
          },
          '100%': {
            'max-width': '9999px',
          },
        },
        flash: {
          '0%': {
            width: '60px',
            opacity: 1,
          },
          '50%': {
            opacity: 0.5,
          },
          '100%': {
            width: '100px',
            opacity: 0,
          },
        },
        jello: {
          '0%': {
            transform: 'scale3d(1, 1, 1)',
          },

          '30%': {
            transform: 'scale3d(1.25, 0.75, 1)',
          },

          '	40%': {
            transform: 'scale3d(0.75, 1.25, 1)',
          },

          '50%': {
            transform: 'scale3d(1.15, 0.85, 1)',
          },

          '65%': {
            transform: 'scale3d(0.95, 1.05, 1)',
          },

          '75%': {
            transform: 'scale3d(1.05, 0.95, 1)',
          },

          '100%': {
            transform: 'scale3d(1, 1, 1)',
          },
        },
        shakeRotate: {
          '0%,100%': {
            transform: 'rotate(0deg)',
            'transform-origin': '50% 50%',
          },

          '10%': {
            transform: 'rotate(8deg)',
          },

          '20%, 40%, 60%': {
            transform: 'rotate(-10deg)',
          },

          '30%, 50%, 70%': {
            transform: 'rotate(10deg)',
          },

          '80%': {
            transform: 'rotate(-8deg)',
          },

          '90%': {
            transform: 'rotate(8deg)',
          },
        },
        spin: {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      },
      animation: {
        fillWidth: 'fillWidth 7s ease-in',
        flash: 'flash 1.2s infinite',
        jello: 'jello 2s ease 3 normal forwards',
        shakeRotate: 'shakeRotate 2s ease 0s infinite',
        spin: 'spin 1s linear infinite',
      },
    },
  },
  plugins: [
    function ({ addUtilities }) {
      addUtilities({
        '.word-break': {
          'word-break': 'break-word',
        },
      });
    },
    function ({ addUtilities }) {
      addUtilities({
        '.bar-chart-scroll': {
          'scrollbar-color': '#1212122e transparent',
          'scrollbar-width': 'thin',
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            'background-color': 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            'background-color': '#1212122e',
            'border-radius': '99999px',
          },
        },
      });
    },
    function ({ addUtilities }) {
      addUtilities({
        '.slider-thumb': {
          '&::-webkit-slider-thumb': {
            'pointer-events': 'auto',
            width: '24px',
            height: '24px',
            'border-radius': '50%',
            '-webkit-appearance': 'none',
            'background-color': '#ffffff',
            cursor: 'pointer',
          },
          '&::-moz-range-thumb': {
            'pointer-events': 'auto',
            width: '24px',
            height: '24px',
            'border-radius': '50%',
            '-webkit-appearance': 'none',
            'background-color': '#ffffff',
            cursor: 'pointer',
          },
          '&::-ms-thumb': {
            'pointer-events': 'auto',
            width: '24px',
            height: '24px',
            'border-radius': '50%',
            '-webkit-appearance': 'none',
            'background-color': '#ffffff',
            cursor: 'pointer',
          },
          '&.thumb-mofid-blue': {
            '&::-webkit-slider-thumb': {
              border: '3px solid #004b69',
            },
            '&::-moz-range-thumb': {
              border: '3px solid #004b69',
            },
            '&::-ms-thumb': {
              border: '3px solid #004b69',
            },
          },
          '&.thumb-neutral': {
            '&::-webkit-slider-thumb': {
              border: '3px solid #282B2E',
            },
            '&::-moz-range-thumb': {
              border: '3px solid #282B2E',
            },
            '&::-ms-thumb': {
              border: '3px solid #282B2E',
            },
          },
          '&.thumb-mofid-green': {
            '&::-webkit-slider-thumb': {
              border: '3px solid #11A699',
            },
            '&::-moz-range-thumb': {
              border: '3px solid #11A699',
            },
            '&::-ms-thumb': {
              border: '3px solid #11A699',
            },
          },
          '&.thumb-small': {
            '&::-webkit-slider-thumb': {
              width: '12px',
              height: '12px',
              border: '1px solid currentColor',
            },
            '&::-moz-range-thumb': {
              width: '12px',
              height: '12px',
              border: '1px solid currentColor',
            },
            '&::-ms-thumb': {
              width: '12px',
              height: '12px',
              border: '1px solid currentColor',
            },
          },
        },
      });
    },
    function ({ addVariant }) {
      addVariant('children', '& > *');
      addVariant('nested-child-ul', '&  ul');
    },
    function ({ addUtilities }) {
      addUtilities({
        '.page-full': {
          position: 'fixed',
          width: '100%',
          height: '100%',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          overflow: 'hidden',
          'overflow-y': 'auto',
        },
        '.ltr': {
          direction: 'ltr',
        },
        '.fill-mode-forwards': {
          'animation-fill-mode': `forwards`,
          '-webkit-animation-fill-mode': `forwards`,
        },
        '.scroll-nice': {
          'scrollbar-color': '#E2E7EA transparent',
          'scrollbar-width': 'thin',
          '&::-webkit-scrollbar': {
            width: '5px',
            'background-color': '#fafafa',
          },
          '&::-webkit-scrollbar-track': {
            border: '1px solid #fafafa',
            'border-radius': '16px',
          },
          '&::-webkit-scrollbar-thumb': {
            'border-radius': '16px',
            'background-color': '#E2E7EA',
          },
        },
        '.default-input-appearance': {
          appearance: 'textfield',
          '&::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          '&::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
        },
        '.scroll-hide': {
          'scrollbar-color': 'transparent transparent',
          'scrollbar-width': 'thin',
          '&::-webkit-scrollbar': {
            width: '5px',
            'background-color': 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            border: '1px solid transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            'background-color': 'transparent',
          },
        },
        '.scroll-none': {
          /* IE and Edge */
          '-ms-overflow-style': 'none',
          /* Firefox */
          'scrollbar-width': 'none',
          /* Safari and Chrome */
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        '.scroll-show': {
          /* IE and Edge */
          '-ms-overflow-style': 'unset',
          /* Firefox */
          'scrollbar-width': 'unset',
          /* Safari and Chrome */
          '&::-webkit-scrollbar': {
            display: 'unset',
          },
        },
        '.hide-range-thumb': {
          '-webkit-appearance': 'none',
          '-moz-appearance': 'none',
          height: '28px',
          width: '28px',
          'background-color': '#ffffff',
          'border-radius': '8px',
          border: '4px solid',
          transition: 'all 0.2ms',
          cursor: 'pointer',
          'box-shadow': '0px 4px 10px rgba(0, 0, 0, 0.15)',
          'z-index': 2,
          position: 'relative',
          'border-color': '#D0D8DD',
        },
        '.range-thumb-red': {
          'border-color': '#f58683',
        },
        '.range-thumb-green': {
          'border-color': '#4dc1b6',
        },
        '.range-thumb-neutral': {
          'border-color': '#D0D8DD',
        },
        '.all-unset': {
          all: 'unset',
        },
        '.transform-3d': {
          'transform-style': 'preserve-3d',
        },
      });
    },
  ],
};
